import { Close, ExpandMore } from '@mui/icons-material'
import { alpha, Box, IconButton, Paper, Stack, useTheme } from '@mui/material'
import { useState } from 'react'

import LoadingPaperSkeleton from '~/components/loadingPaperSkeleton'
import AgentChatInfo from '~/features/agents/components/agentInfoHeader/agentChatInfo'
import AgentEmailInfo from '~/features/agents/components/agentInfoHeader/agentEmailInfo'
import AgentNameInfo from '~/features/agents/components/agentInfoHeader/agentNameInfo'
import AgentPhoneInfo from '~/features/agents/components/agentInfoHeader/agentPhoneInfo'
import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import { EAgentKeys } from '~/features/agents/models'
import { ECapability } from '~/features/user/models'
import useCapabilities from '~/hooks/useCapabilities'
import { store } from '~/store'
import rem from '~/utils/ui'

const TRANSITION_DELAY = '0.3s'

function AgentInfoHeader() {
  const theme = useTheme()
  const { canRead } = useCapabilities()
  const { isLoading, isError, selectedAgent } = useGetAgentCentralized()

  const [isExpanded, setIsExpanded] = useState(false)

  const [tier, isAgentInfoHeaderOpen, setUIElements] = store((state) => [
    state.organization.paymentTier,
    state.UIElements.isAgentInfoHeaderOpen,
    state.setUIElements,
  ])

  if (isLoading) {
    return (
      <LoadingPaperSkeleton
        sx={{
          position: 'sticky',
          height: { xxxs: rem(177), lg: rem(92) },
          backgroundColor: theme.palette.mainBackground,
        }}
      />
    )
  }

  if (
    isError ||
    !selectedAgent ||
    !selectedAgent[EAgentKeys.id] ||
    !canRead(ECapability.aiAssistantConfig)
  ) {
    return null
  }

  return (
    <Paper
      elevation={3}
      sx={{
        padding: {
          xxxs: `${rem(20)} ${rem(16)}`,
          sm: `${rem(20)} ${rem(24)}`,
          lg: `${rem(20)} ${rem(28)} ${rem(9)} ${rem(28)}`,
        },
        backgroundColor: theme.palette.mainBackground,
        width: '100%',
        position: 'sticky',
        top: 0,
        zIndex: 2,
        flexShrink: 0,
        display: 'grid',
        gridTemplateRows: isAgentInfoHeaderOpen ? '1fr' : '0fr',
        overflow: 'hidden',
        transform: isAgentInfoHeaderOpen
          ? 'translateY(0)'
          : 'translateY(-100%)',
        transition: `
          transform ${TRANSITION_DELAY} ease-in-out,
          grid-template-rows 0.2s ease-in-out ${isAgentInfoHeaderOpen ? '0s' : TRANSITION_DELAY},
          padding-block 0.2s ease-in-out ${isAgentInfoHeaderOpen ? '0s' : TRANSITION_DELAY}
        `,
        ...(isAgentInfoHeaderOpen
          ? {}
          : { '@media (min-width: 0px)': { paddingBlock: 0 } }),
      }}
    >
      <IconButton
        onClick={() => setUIElements({ isAgentInfoHeaderOpen: false })}
        size="small"
        sx={{
          position: 'absolute',
          top: rem(10),
          right: rem(10),
          '& .MuiTouchRipple-root': {
            color: theme.palette.tertiary2,
          },
          '&:hover': {
            backgroundColor: alpha(theme.palette.tertiary2, 0.3),
          },
        }}
      >
        <Close
          sx={{
            width: rem(20),
            height: rem(20),
            color: theme.palette.tertiary1,
          }}
        />
      </IconButton>
      <Stack
        direction={{ xxxs: 'column', lg: 'row' }}
        sx={{
          columnGap: { xxxs: 1, lg: 11 },
          rowGap: 1,
          flexWrap: 'wrap',
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: { xxxs: '100%', lg: 'auto' },
            justifyContent: 'space-between',
          }}
        >
          <AgentNameInfo />

          <IconButton
            onClick={() => setIsExpanded((prev) => !prev)}
            sx={{
              marginRight: isAgentInfoHeaderOpen ? rem(15) : 0,
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
              marginBottom: 'auto',
              display: { xxxs: 'flex', lg: 'none' },
            }}
          >
            <ExpandMore />
          </IconButton>
        </Box>

        <Stack
          direction={{ xxxs: 'column', lg: 'row' }}
          spacing={{ xxxs: isExpanded ? 2 : 1, lg: 6 }}
        >
          <AgentPhoneInfo isExpanded={isExpanded} tier={tier} />
          <AgentEmailInfo isExpanded={isExpanded} tier={tier} />
          <AgentChatInfo isExpanded={isExpanded} />
        </Stack>
      </Stack>
    </Paper>
  )
}

export default AgentInfoHeader
